import React, { useState } from 'react';
import { Box, Typography, Chip, Button, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Skeleton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShiftFormModal from '../../../components/ShiftFormModal';
import { useSelector } from 'react-redux';
import EditAssignmentModal from '../../../components/EditAssignmentModal';
import { StaffService } from '../../../api/services/staffService';

export default function DutyScheduleTab({
    staffMembers,
    shifts,
    scheduleEvents,
    isLoadingSchedule,
    handleSuccess,
    scheduleDate,
    setScheduleDate,
    setShifts,
    setNotification,
    handleOpenAssignModal
}) {
    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = authData.tenantId || '1';
    const { tenants } = useSelector((state) => state.auth || { tenants: [] });
    const tenant = tenants.find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));
    const hotelStatus = tenant?.status || 'Active';
    const isShiftLocked = hotelStatus === 'Inactive' || hotelStatus === 'Suspended';
    const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
    const [isEditAssignmentOpen, setIsEditAssignmentOpen] = useState(false);
    const [isDeleteShiftOpen, setIsDeleteShiftOpen] = useState(false);
    const [isDeletingShift, setIsDeletingShift] = useState(false);
    const [shiftToDelete, setShiftToDelete] = useState(null);
    const [selectedShift, setSelectedShift] = useState(null);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [isManageShiftsOpen, setIsManageShiftsOpen] = useState(false);

    const formatLocalDate = (d) => {
        if (!d) return '';
        if (typeof d === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
            if (d.includes('T')) return d.split('T')[0];
            const parsed = new Date(d);
            if (!isNaN(parsed.getTime())) {
                const year = parsed.getFullYear();
                const month = String(parsed.getMonth() + 1).padStart(2, '0');
                const day = String(parsed.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }
            return d;
        }
        if (d instanceof Date && !isNaN(d.getTime())) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
        return '';
    };

    const getWeekRange = (date) => {
        const start = new Date(date);
        const day = start.getDay();
        const diff = start.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(start.setDate(diff));
        monday.setHours(0,0,0,0);
        
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23,59,59,999);
        
        return { 
            start: formatLocalDate(monday), 
            end: formatLocalDate(sunday),
            days: Array.from({length: 7}, (_, i) => {
                const d = new Date(monday);
                d.setDate(monday.getDate() + i);
                return d;
            })
        };
    };

    const handleOpenEditAssignment = (assignment, staff) => {
        setSelectedAssignment({
            ...assignment,
            staff: assignment?.staff || staff,
            fullName: assignment?.fullName || staff?.fullName || staff?.name,
            name: assignment?.name || staff?.name || staff?.fullName,
            department: assignment?.department || staff?.department,
            departmentId: assignment?.departmentId || staff?.departmentId || staff?.department?.id
        });
        setIsEditAssignmentOpen(true);
    };

    const refreshShifts = async () => {
        try {
            const data = await StaffService.getShifts({ noPagination: true });
            const list = Array.isArray(data) ? data : (data?.data || data?.shifts || []);
            setShifts(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error('Failed to refresh shifts:', err);
        }
    };

    const handleOpenManageShifts = () => {
        refreshShifts();
        setIsManageShiftsOpen(true);
    };

    const handleShiftSuccess = async (msg) => {
        if (handleSuccess) handleSuccess(msg);
        await refreshShifts();
    };

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" fontWeight="bold">Weekly Roster</Typography>
                    <Chip 
                        label={`${getWeekRange(scheduleDate).start} to ${getWeekRange(scheduleDate).end}`} 
                        variant="outlined" 
                        size="small" 
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={isShiftLocked ? "Scheduling is locked while property is Inactive/Suspended" : ""}>
                        <span>
                            <Button 
                                size="small" 
                                variant="contained" 
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenAssignModal()}
                                disabled={isShiftLocked}
                            >
                                Assign Shift
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={isShiftLocked ? "Scheduling is locked while property is Inactive/Suspended" : ""}>
                        <span>
                            <Button 
                                size="small" 
                                variant="outlined" 
                                startIcon={<AccessTimeIcon />}
                                onClick={handleOpenManageShifts}
                                disabled={isShiftLocked}
                            >
                                Manage Shifts
                            </Button>
                        </span>
                    </Tooltip>
                    <Button 
                        size="small" 
                        variant="outlined"
                        onClick={() => {
                            const d = new Date(scheduleDate);
                            d.setDate(d.getDate() - 7);
                            setScheduleDate(d);
                        }}
                    >
                        Previous Week
                    </Button>
                    <Button 
                        size="small" 
                        variant="outlined"
                        onClick={() => setScheduleDate(new Date())}
                    >
                        Today
                    </Button>
                    <Button 
                        size="small" 
                        variant="outlined"
                        onClick={() => {
                            const d = new Date(scheduleDate);
                            d.setDate(d.getDate() + 7);
                            setScheduleDate(d);
                        }}
                    >
                        Next Week
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 200 }}>Staff Member</TableCell>
                            {getWeekRange(scheduleDate).days.map((day, i) => (
                                <TableCell key={i} align="center">
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                        {day.toLocaleDateString('en-US', { weekday: 'short' })}
                                    </Typography>
                                    {day.getDate()}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoadingSchedule ? (
                            [1, 2, 3, 4, 5].map(i => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton width={120} /></TableCell>
                                    {[1, 2, 3, 4, 5, 6, 7].map(j => (
                                        <TableCell key={j}><Skeleton variant="rectangular" height={40} /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : scheduleEvents.length > 0 ? (
                            scheduleEvents.map((staffObj, idx) => (
                                <TableRow key={staffObj.id || staffObj._id || idx} hover>
                                    <TableCell><b>{staffObj.fullName || staffObj.name || 'Unknown'}</b></TableCell>
                                    {getWeekRange(scheduleDate).days.map((day, i) => {
                                        const dateStr = formatLocalDate(day);
                                        const dayEvents = (staffObj.dutySchedules || []).filter(e => {
                                            const eventDate = e.date ? e.date.split('T')[0] : (e.startDate ? e.startDate.split('T')[0] : '');
                                            return eventDate === dateStr;
                                        });
                                        
                                        return (
                                            <TableCell key={i} align="center">
                                                {dayEvents.map((e, ei) => (
                                                    <Tooltip key={ei} title={`${e.shift?.name || 'Shift'}: ${e.shift?.startTime || '--:--'} - ${e.shift?.endTime || '--:--'}`}>
                                                        <Chip 
                                                            label={e.shift?.name || 'Duty'} 
                                                            size="small" 
                                                            color="primary"
                                                            variant="soft"
                                                            sx={{ fontSize: '0.65rem', height: 20, mb: 0.5, cursor: 'pointer' }}
                                                            onClick={() => handleOpenEditAssignment(e, staffObj)}
                                                        />
                                                    </Tooltip>
                                                ))}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                    <Typography variant="body2" color="text.secondary">No duty assignments found for this week.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Manage Shift Templates Dialog */}
            <Dialog
                open={isManageShiftsOpen}
                onClose={() => setIsManageShiftsOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon color="primary" /> Manage Shift Templates
                    </Box>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setSelectedShift(null);
                            setIsShiftModalOpen(true);
                        }}
                    >
                        Add Shift Template
                    </Button>
                </DialogTitle>
                <DialogContent dividers>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Shift Name</TableCell>
                                    <TableCell>Timing</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {console.log(
                                    shifts,"shifts"
                                )}
                                {shifts && shifts.length > 0 ? (
                                    shifts.map((shift) => (
                                        <TableRow key={shift.id || shift._id} hover>
                                            <TableCell><b>{shift.name}</b></TableCell>
                                            <TableCell>{shift.startTime} - {shift.endTime}</TableCell>
                                            <TableCell>{shift.department?.name || shift.departmentName || shift.department || 'All Departments'}</TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Edit Shift">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => {
                                                            setSelectedShift(shift);
                                                            setIsShiftModalOpen(true);
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Shift">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => {
                                                            setShiftToDelete(shift);
                                                            setIsDeleteShiftOpen(true);
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No shift templates found. Click "Add Shift Template" to configure one.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Button onClick={() => setIsManageShiftsOpen(false)} color="inherit">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <ShiftFormModal
                open={isShiftModalOpen}
                onClose={() => setIsShiftModalOpen(false)}
                onSuccess={handleShiftSuccess}
                shiftData={selectedShift}
            />

            <EditAssignmentModal
                open={isEditAssignmentOpen}
                onClose={() => setIsEditAssignmentOpen(false)}
                onSuccess={handleSuccess}
                shiftList={shifts}
                staffList={staffMembers || []}
                assignmentData={selectedAssignment}
            />

            <Dialog
                open={isDeleteShiftOpen}
                onClose={() => setIsDeleteShiftOpen(false)}
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>Delete Shift Template</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the <strong>{shiftToDelete?.name}</strong> template? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, bgcolor: 'grey.50' }}>
                    <Button onClick={() => setIsDeleteShiftOpen(false)} color="inherit" disabled={isDeletingShift}>Cancel</Button>
                    <Button 
                        onClick={async () => {
                            setIsDeletingShift(true);
                            try {
                                const idToDelete = shiftToDelete.id || shiftToDelete._id;
                                await StaffService.deleteShift(idToDelete);
                                setShifts((prevShifts) => prevShifts.filter((s) => (s.id || s._id) !== idToDelete));
                                setNotification({ open: true, message: 'Shift deleted successfully', severity: 'success' });
                                setIsDeleteShiftOpen(false);
                            } catch (err) {
                                setNotification({ open: true, message: err.message || 'Failed to delete shift', severity: 'error' });
                            } finally {
                                setIsDeletingShift(false);
                            }
                        }} 
                        color="error" 
                        variant="contained"
                        disabled={isDeletingShift}
                        startIcon={isDeletingShift && <CircularProgress size={16} color="inherit" />}
                    >
                        {isDeletingShift ? 'Deleting...' : 'Delete Shift'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
